import { Path } from './path';
import { Vec2 } from './vec';
import { EnemyType, StatusEffect } from './types';
import { slowFactor, tickStatuses, vulnerabilityBonus } from './status';
import type { ChipKind } from './chip';
import type { TowerPoisonSpec } from './towerPoison';

/**
 * Every enemy that IS a nacho — what Space Debris jams (spec
 * docs/superpowers/specs/2026-09-10-nacho-stages-design.md). While the effect
 * runs none of these can touch a tower: no thrown knife, no laser, no stun.
 * Plain `nacho` is passive and only walks, but it belongs to the family, so a
 * future ability of his is covered without anyone remembering this list.
 * Pinned to balance.ts by tests/unit/balance.test.ts.
 *
 * PRINCE NACHO IS DELIBERATELY ABSENT (both forms). He is a nacho by name and
 * by sprite, but Space Debris is a stored consumable and the chapter finale
 * must not be switchable-off for two of its minutes — see spec
 * docs/superpowers/specs/2026-09-11-through-space-and-time-design.md §2.
 */
/**
 * Bosses whose abilities the Hub arenas' "Disable boss abilities" toggle mutes
 * (spec 2026-10-08): Chiply's Dad's chips, The Lifeguard's whistles, both of
 * Prince Nacho's forms (spears, laser, barrage and its shields) and The Shadow
 * Reaper's scythe. They still walk, float and take damage.
 */
export const BOSS_ABILITY_TYPES: readonly EnemyType[] = [
  'chiplysdad',
  'lifeguard',
  'princenacho',
  'princenacho2',
  'reaper',
  // His falling winged turtles (spec 2026-10-12).
  'lordturtleshell',
  // His thrown mushrooms (spec 2026-10-22). His dodge is how he takes
  // damage, not an attack, and stays on.
  'themushy',
  // His sky drops, his minions and his Hyno-Fungi (spec 2026-10-27).
  'kingshroometron',
];

export const NACHO_TYPES: readonly EnemyType[] = [
  'nacho',
  'moonnacho',
  'spicednacho',
  'lasernacho',
  // The Dark Shadows' Dark Nacho (spec 2026-10-07): an ordinary walking nacho,
  // so Space Debris jams his chips and his stun like the Moon's.
  'darknacho',
];

/**
 * One entry of an `empower` aura: what a carrier does to one enemy type while
 * it is alive. Every buff field is optional — omit one and that trait keeps the
 * target's own number.
 */
export interface EmpowerBuff {
  enemyType: EnemyType;
  /** Boosted max HP. Unset = the target keeps its own. */
  hp?: number;
  /** Boosted `towerFire` cadence in seconds. Unset = the target keeps its own. */
  towerFireCooldown?: number;
  /** Boosted `stun` duration in seconds. Unset = the target keeps its own. */
  stunDuration?: number;
}

export interface EnemySpec {
  hp: number;
  speed: number;
  bounty: number;
  /** Lives drained when this enemy leaks past the exit. Defaults to 1. */
  livesCost?: number;
  /**
   * Tower-stun ability (the Null Nugget's hex): every `cooldown` seconds this
   * enemy disables `count` random towers (default 1) for `duration` seconds.
   * The Esparagus Mage takes two at a stroke; one trigger never picks the same
   * tower twice.
   */
  stun?: {
    duration: number;
    cooldown: number;
    count?: number;
    /**
     * When true the cooldown is shared across every LIVE carrier of this enemy
     * type rather than run per-enemy: the group fires once per `cooldown`,
     * taking `count` towers, however many carriers are on the field. The
     * Esparagus Mage uses this — three mages each on their own 10s clock stunned
     * two towers three times per 10s, which is not what "two towers every ten
     * seconds" means.
     */
    shared?: boolean;
  };
  /**
   * "Cannot be slowed by any effect" (King Nugget-tron): slow auras never take
   * hold and a Wise-Hambi map freeze does not halt this enemy — it walks (and
   * hexes) straight through both.
   */
  slowImmune?: boolean;
  /**
   * Veggie Hex (the Brocco): every `cooldown` seconds this enemy slows one
   * random tower's fire rate by `slow` (0..1, e.g. 0.5 = 50% slower) for
   * `duration` seconds. No tower is immune.
   */
  fireRateHex?: { slow: number; duration: number; cooldown: number };
  /**
   * Imperial Decree (Emperor Pepro): every `cooldown` seconds this enemy seals
   * tower placement map-wide for `duration` seconds — placeTower is rejected
   * while the seal holds. Existing towers keep fighting; only NEW placements
   * are blocked.
   */
  placementLock?: { duration: number; cooldown: number };
  /**
   * Minion summon (Emperor Pepro): every `cooldown` seconds this enemy spawns
   * up to `count` enemies of `enemyType`, but never grows that type past
   * `maxAlive` alive at once. Minions enter at the summoner's own position on
   * the path, unless `fromBase` is set — then they enter at the path start
   * (the enemy base / temple entrance) and walk the whole route.
   */
  summon?: {
    enemyType: EnemyType;
    count: number;
    cooldown: number;
    maxAlive: number;
    fromBase?: boolean;
    /**
     * More types summoned on the SAME trigger, `count` of each, each capped
     * at `maxAlive` alive (King Shroometron: a Glowshroom AND a Poison Shroom
     * every 10 s).
     */
    also?: EnemyType[];
  };
  /**
   * Once, the first tick this enemy is at or below `belowHpFraction` of its
   * max HP, it summons `count` `enemyType` at its own spot (King Shroometron
   * calls a Hyno-Fungi at a quarter health).
   */
  hpSummon?: { belowHpFraction: number; enemyType: EnemyType; count: number };
  /**
   * Hypnosis (the Hyno-Fungi, spec 2026-10-27): every `cooldown` seconds it
   * takes 1 to `maxTowers` random towers, and each suffers ONE of four
   * outcomes at equal odds — stunned `stun` s; shroomnitized (the Glowshroom's
   * possession, `possess`); range × `rangeMult` for `rangeFor` s; or stunned
   * `stun` s AND tower placement sealed `seal` s.
   */
  hypnotize?: {
    cooldown: number;
    maxTowers: number;
    stun: number;
    seal: number;
    rangeMult: number;
    rangeFor: number;
    possess: { hits: number; damage: number; first: number; gap: number; projectile: string };
  };
  /**
   * Sky drop (King Shroometron): as his HP crosses each of `atHpFractions`,
   * `count` mushrooms fall from the top of the screen, one every `gap`
   * seconds; each takes `fuse` seconds to reach the bottom. A tapped one is
   * gone; one that lands hits a random tower for `towerDamage` HP. Rides the
   * press-target machinery as chips of kind `skyshroom`.
   */
  skyDrop?: {
    atHpFractions: number[];
    count: number;
    gap: number;
    fuse: number;
    towerDamage: number;
  };
  /**
   * Ember Barrage (King Ember): the only ability that DESTROYS towers rather
   * than disabling them. While a carrier is alive on the field every tower has
   * HP (see TOWER_MAX_HP) and shows a bar; every `cooldown` seconds the carrier
   * launches a fireball at one random living tower, and `travel` seconds later
   * it lands and costs that tower `damage` HP. A tower reduced to 0 HP is
   * removed and its plot frees up for a full-price rebuild.
   */
  towerFire?: {
    damage: number;
    cooldown: number;
    travel: number;
    /**
     * Atlas key of the thrown thing (the Corroder's leaves, the Nuisance
     * Nugget's nugget piece). Unset = King Ember's drawn blue fireball.
     * Render-only; the sim carries it through on the FireballState.
     */
    projectile?: string;
    /**
     * Draw the shot as a BEAM — a bright line snapped straight from the
     * carrier to the tower that fades over `travel` — rather than a thrown
     * object arcing across the field (the Laser Nacho). Render-only: the sim
     * still resolves the hit `travel` seconds later, exactly as for a throw.
     */
    beam?: boolean;
    /**
     * Strike this many DISTINCT living towers per trigger instead of one
     * (Prince Nacho's second form: three). Mirrors `stun.count` — each victim
     * leaves the pool, so one trigger never hits the same tower twice, and a
     * short pool simply means fewer victims. Unset = 1.
     */
    count?: number;
    /**
     * The shot FALLS: it launches from directly above its target, off the top
     * of the screen, and comes straight down rather than arcing across the
     * field from the carrier (Prince Nacho's golden spears). Affects only
     * where the shot starts and the shape of its flight — the sim still
     * resolves the hit `travel` seconds later, exactly as for a throw.
     */
    fromSky?: boolean;
    /**
     * Draw the thrown sprite this many times its normal size (Prince Nacho's
     * second form: one HUGE spear art key at 1.8×, rather than a second key
     * for the same picture). Render-only; 1 when unset.
     */
    scale?: number;
    /**
     * Hit EVERY living tower at once (The Shadow Reaper's scythe), not
     * `count` of them. Same pick-and-remove loop, so the order of the hits is
     * still drawn from the seeded Rng.
     */
    everyTower?: boolean;
    /**
     * Render-only: the carrier SWINGS rather than throws — nothing flies, the
     * enemy plays its swing and every struck tower flashes "KO" as the hit
     * lands `travel` seconds later. The sim resolves it exactly like a throw.
     */
    swing?: boolean;
    /**
     * The shot POISONS the tower it lands on (the Poison Shroom): it takes
     * `damage` every `every` seconds, `ticks` times, after an impact that
     * deals the shot's own `damage` (0 for a poison-only attacker). A fresh
     * dose refreshes a running one. See core/towerPoison.
     */
    poison?: TowerPoisonSpec;
    /**
     * The shot SHROOMNITIZES the tower it lands on (the Glowshroom's potion):
     * the tower is possessed exactly as by `ghosting` — it stops attacking and
     * strikes `hits` other towers for `damage`, `first` seconds after landing
     * then every `gap` — and its strikes fly as `projectile`. A potion that
     * lands on a tower already possessed does nothing. The impact deals the
     * shot's own `damage` (0 for the Glowshroom).
     */
    possess?: { hits: number; damage: number; first: number; gap: number; projectile: string };
    /**
     * Only towers within this many px of the carrier are candidates (the
     * Zombieshroom's knife, 100). A charged throw with nobody in reach is HELD
     * and goes the tick a tower comes in range. Unset = any tower on the map.
     */
    range?: number;
  };
  /**
   * Dodge (The Mushy): each tower hit — an impact, a splash, a melee blow —
   * misses him outright with this chance, statuses and all. Rolled on his own
   * seeded stream (`rollDodge`), so no other enemy's numbers move. Damage
   * over time already on him is not a hit and is never dodged.
   */
  dodge?: { chance: number };
  /**
   * Ghosting (Jack'O Ghost, the Blue Ghost; spec 2026-10-07): every
   * `cooldown` seconds this enemy POSSESSES one random tower that is not
   * possessed already. A possessed tower stops attacking enemies; `first`
   * seconds later it hits another random tower for `damage` HP, then again
   * every `gap` seconds until it has struck `hits` DIFFERENT towers, and the
   * possession lifts. A hit with no other tower left to strike fizzles.
   * Only does harm where tower HP is armed (every Origin level).
   */
  ghosting?: { cooldown: number; hits: number; damage: number; first: number; gap: number };
  /**
   * Where an `anchored` enemy appears when a WAVE sends it (The Shadow
   * Reaper). A wave spawn otherwise starts at the mouth of the trail, which
   * would pin an anchored enemy there for good. (`transformsInto` carries
   * its own `at`.)
   */
  anchorAt?: Vec2;
  /**
   * Empower aura (The Microwave): while a carrier is alive on the field, every
   * living enemy of each listed `enemyType` is boosted — `hp` raises its max HP
   * (one already walking gains the difference on the spot), `towerFireCooldown`
   * makes it throw on that cadence instead of its own, and `stunDuration`
   * lengthens the towers it locks down. Every field is optional, so a buff can
   * be cadence-only (the Moon and Laser Nachos) or HP-and-stun (the Spiced
   * Nacho). Reverts (max HP back, HP clamped) the tick after the last carrier
   * is gone.
   */
  empower?: EmpowerBuff[];
  /**
   * Press-target drop (Chiply's Dad's chips, The Lifeguard's whistles): every
   * `cooldown` seconds this enemy drops a target at a random spot on the
   * playfield, and the player has `fuse` seconds to press it or lose `lives`
   * lives (see core/chip.ts). `kind` defaults to 'chip'. With `exclusive`
   * the cooldown does not tick while one of this enemy's own targets is up,
   * and restarts when it resolves — one at a time, `cooldown` seconds apart.
   */
  chipDrop?: {
    cooldown: number;
    fuse: number;
    lives: number;
    kind?: ChipKind;
    exclusive?: boolean;
  };
  /**
   * This enemy does not walk (Prince Nacho's second form, which floats above
   * the track and never moves). `updateEnemy` leaves its position alone, its
   * `remaining` pinned at 0 and `reachedEnd` forever false — so it can never
   * leak, and the level cannot end while it is alive.
   *
   * It is otherwise an ORDINARY enemy: projectiles, melee lunges, splash and
   * every damage-over-time reach it unchanged.
   */
  anchored?: boolean;
  /**
   * Every tower can hit this enemy however far away it is — `selectTarget`
   * skips its range test (Prince Nacho's second form: "all your towers attack
   * prince nacho second form"). With `remaining` at 0 an anchored enemy
   * already sorts first, so the whole board engages it without a further rule.
   */
  ignoreRange?: boolean;
  /**
   * A second form. When this enemy is KILLED (not leaked) the sim spawns
   * `enemyType` anchored at `at`, in the same step, before the win check — a
   * one-tick gap there would end the level on a technicality. Spawning also
   * clears the wave runners: "when he spawns no enemys spawn on the track".
   */
  transformsInto?: { enemyType: EnemyType; at: Vec2 };
  /**
   * Halolight (spec 2026-10-08): when this enemy is KILLED (not leaked) it
   * splits into `count` smaller `enemyType`s right where it died, on its own
   * route — in the same step, before the win check, exactly like
   * `transformsInto`. Unlike a transform it cancels nothing. The children are
   * centred on the parent, `spread` px of track between the first and the
   * last; halving it at each generation keeps the grandchildren evenly spaced
   * instead of landing two on one spot.
   */
  splitsInto?: { enemyType: EnemyType; count: number; spread: number };
  /**
   * Prince Nacho's second form, below half health: every `placementLock`
   * trigger becomes a BARRAGE of `count` lasers `gap` seconds apart, each one
   * sealing placement AND dropping a shield the player must hold (see
   * core/shield.ts). While a barrage runs this enemy's other clocks are
   * frozen — "Prince nacho can not use his other attacks during the laser
   * attacks" — and no second barrage can start.
   *
   * Needs `placementLock`, which supplies the cadence and the seal duration.
   */
  laserBarrage?: { count: number; gap: number; belowHpFraction: number };
}

export interface EnemyState {
  id: number;
  type: EnemyType;
  hp: number;
  maxHp: number;
  baseSpeed: number;
  bounty: number;
  /** Lives drained when this enemy leaks past the exit. */
  livesCost: number;
  /** Immune to slow auras and to the map-wide freeze (see EnemySpec.slowImmune). */
  slowImmune?: boolean;
  distance: number;
  /**
   * Which of the level's routes this enemy walks (an index into GameSim's
   * `routes`). 0 on every single-entrance level — only Nach'yo Business's two
   * nacho bases make it anything else. See `LevelDef.entrances`.
   */
  route: number;
  /**
   * Distance still to walk before this enemy reaches the base. `distance`
   * alone cannot be compared across routes of different length, so targeting
   * ranks by this instead — smaller is closer to breaking through. Kept in
   * step by `updateEnemy`.
   */
  remaining?: number;
  pos: Vec2;
  /** Current velocity (px/sec) along the path — used by towers to lead shots. */
  vel: Vec2;
  statuses: StatusEffect[];
  alive: boolean;
  reachedEnd: boolean;
  /**
   * Seconds until this enemy's next tower stun (only meaningful for enemies
   * whose spec carries `stun`; undefined otherwise). Managed by GameSim.
   */
  stunCooldown?: number;
  /**
   * Seconds until this enemy's next Veggie Hex (only meaningful for enemies
   * whose spec carries `fireRateHex`; undefined otherwise). Managed by GameSim.
   */
  hexCooldown?: number;
  /**
   * Seconds until this enemy's next placement seal (only meaningful for enemies
   * whose spec carries `placementLock`; undefined otherwise). Managed by GameSim.
   */
  lockCooldown?: number;
  /**
   * Seconds until this enemy's next minion summon (only meaningful for enemies
   * whose spec carries `summon`; undefined otherwise). Managed by GameSim.
   */
  summonCooldown?: number;
  /**
   * Seconds until this enemy's next Ember Barrage fireball (only meaningful for
   * enemies whose spec carries `towerFire`; undefined otherwise). Managed by
   * GameSim.
   */
  towerFireCooldown?: number;
  /** Currently boosted by an `empower` carrier (see EnemySpec.empower). Managed by GameSim. */
  empowered?: boolean;
  /**
   * Seconds until this enemy's next chip drop (only meaningful for enemies
   * whose spec carries `chipDrop`; undefined otherwise). Managed by GameSim.
   */
  chipCooldown?: number;
  /** Never walks; never leaks (see EnemySpec.anchored). */
  anchored?: boolean;
  /** Every tower reaches it, whatever its range (see EnemySpec.ignoreRange). */
  ignoreRange?: boolean;
  /**
   * Seconds until this enemy's next laser (only meaningful for the enemy whose
   * spec carries `placementLock`); undefined otherwise. Managed by GameSim.
   */
  laserCooldown?: number;
  /**
   * Seconds until this enemy's next possession (only meaningful for enemies
   * whose spec carries `ghosting`); undefined otherwise. Managed by GameSim.
   */
  ghostCooldown?: number;
  /** Chance a tower hit misses (see EnemySpec.dodge); undefined for everyone else. */
  dodgeChance?: number;
  /** State of this enemy's own dodge stream (mulberry32), seeded from its id. */
  dodgeSeed?: number;
  /** Dodges since GameSim last turned them into `enemyDodged` events. */
  dodged?: number;
  /**
   * SHROOMNITIZED by a Snowshroom tower (spec 2026-10-24): `in` seconds until
   * this enemy hits the nearest other enemy once, then it is cleared.
   */
  shroomnitized?: { in: number };
  /** Present only for `hypnotize` carriers (the Hyno-Fungi): seconds until the next. */
  hypnoCooldown?: number;
  /** Set once an `hpSummon` has fired, so it never fires twice. */
  hpSummoned?: boolean;
  /** `skyDrop`: how many HP thresholds have triggered so far. */
  skyDropsDone?: number;
  /** `skyDrop`: mushrooms still to fall in the running drop, and when the next does. */
  skyDropQueue?: { left: number; nextIn: number };
}

/**
 * Does this hit miss? Only an enemy with `dodgeChance` ever rolls, on a
 * stream of its own: the sim's shared Rng is never drawn, so every seeded
 * test and replay without The Mushy is bit-for-bit what it was.
 */
export function rollDodge(e: EnemyState): boolean {
  if (!e.dodgeChance || !e.alive) return false;
  e.dodgeSeed = ((e.dodgeSeed ?? 0) + 0x6d2b79f5) >>> 0;
  let t = e.dodgeSeed;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  if (r >= e.dodgeChance) return false;
  e.dodged = (e.dodged ?? 0) + 1;
  return true;
}

export function createEnemy(
  id: number,
  type: EnemyType,
  spec: EnemySpec,
  path: Path,
  // Summoned minions enter mid-path at their summoner's position; wave spawns
  // always start at the entrance (0).
  distance = 0,
  // Which route of the level `path` is (see EnemyState.route).
  route = 0,
): EnemyState {
  return {
    id,
    type,
    hp: spec.hp,
    maxHp: spec.hp,
    baseSpeed: spec.speed,
    bounty: spec.bounty,
    livesCost: spec.livesCost ?? 1,
    slowImmune: spec.slowImmune ?? false,
    distance,
    route,
    // An anchored enemy has nothing left to walk, which also sorts it FIRST
    // under targeting's `toBase` — every tower prefers it.
    remaining: spec.anchored ? 0 : path.length - distance,
    pos: path.pointAt(distance),
    vel: { x: 0, y: 0 },
    statuses: [],
    alive: true,
    reachedEnd: false,
    // First hex lands a full cooldown after spawning (not instantly).
    stunCooldown: spec.stun ? spec.stun.cooldown : undefined,
    hexCooldown: spec.fireRateHex ? spec.fireRateHex.cooldown : undefined,
    lockCooldown: spec.placementLock ? spec.placementLock.cooldown : undefined,
    summonCooldown: spec.summon ? spec.summon.cooldown : undefined,
    towerFireCooldown: spec.towerFire ? spec.towerFire.cooldown : undefined,
    chipCooldown: spec.chipDrop ? spec.chipDrop.cooldown : undefined,
    ghostCooldown: spec.ghosting ? spec.ghosting.cooldown : undefined,
    ...(spec.hypnotize ? { hypnoCooldown: spec.hypnotize.cooldown } : {}),
    ...(spec.dodge
      ? { dodgeChance: spec.dodge.chance, dodgeSeed: Math.imul(id + 1, 0x9e3779b1) >>> 0 }
      : {}),
    ...(spec.anchored ? { anchored: true } : {}),
    ...(spec.ignoreRange ? { ignoreRange: true } : {}),
  };
}

export function damageEnemy(e: EnemyState, amount: number): void {
  if (!e.alive) return;
  const effective = amount * (1 + vulnerabilityBonus(e.statuses));
  e.hp = Math.max(0, e.hp - effective);
  if (e.hp === 0) e.alive = false;
}

export function updateEnemy(e: EnemyState, dt: number, path: Path, frozen = false): void {
  if (!e.alive) return;
  const burn = tickStatuses(e.statuses, dt);
  if (burn > 0) damageEnemy(e, burn);
  if (!e.alive) return;
  // An anchored enemy (Prince Nacho's second form) never walks: its position is
  // wherever it was spawned, `remaining` stays 0 and it can never reach the end.
  if (e.anchored) {
    e.vel = { x: 0, y: 0 };
    e.remaining = 0;
    return;
  }
  // Map-wide freeze: DoT still ticks (above), but movement is suspended — unless
  // this enemy is slow-immune, which walks straight through the freeze.
  if (frozen && !e.slowImmune) {
    e.vel = { x: 0, y: 0 };
    return;
  }
  const prev = e.pos;
  // Slow-immune enemies ignore slow auras entirely (factor pinned to 1).
  const slow = e.slowImmune ? 1 : slowFactor(e.statuses);
  e.distance += e.baseSpeed * slow * dt;
  e.remaining = path.length - e.distance;
  e.pos = path.pointAt(e.distance);
  e.vel = dt > 0 ? { x: (e.pos.x - prev.x) / dt, y: (e.pos.y - prev.y) / dt } : { x: 0, y: 0 };
  if (e.distance >= path.length) {
    e.reachedEnd = true;
    e.alive = false;
  }
}
